import { useState } from 'react';
import { DECKS } from '../data/deckConfig';
import { getDeckStats, isDuplicateCard } from '../utils/deckUtils';

const FREE_CUSTOM_LIMIT = DECKS.custom.freeLimit;

/* Stroke icons on a 24px grid — no emoji, matching the rest of the system. */
const Icon = ({ path, size = 18, width = 2.4 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={width}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {path}
  </svg>
);

const IconDecks = (p) => <Icon {...p} path={<><path d="m12 2 9 5-9 5-9-5 9-5Z" /><path d="m3 12 9 5 9-5" /><path d="m3 17 9 5 9-5" /></>} />;
const IconProgress = (p) => <Icon {...p} path={<><path d="M3 21h18" /><path d="M6 21V11" /><path d="M12 21V5" /><path d="M18 21v-7" /></>} />;
const IconPlus = (p) => <Icon {...p} path={<><path d="M12 5v14" /><path d="M5 12h14" /></>} />;
const IconLock = (p) => <Icon {...p} path={<><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>} />;
const IconCheck = (p) => <Icon {...p} path={<path d="M20 6 9 17l-5-5" />} />;
const IconClose = (p) => <Icon {...p} path={<><path d="M18 6 6 18" /><path d="m6 6 12 12" /></>} />;
const IconInfo = (p) => <Icon {...p} path={<><circle cx="12" cy="12" r="9" /><path d="M12 16v-5" /><path d="M12 8h.01" /></>} />;
const IconTrash = (p) => <Icon {...p} path={<><path d="M3 6h18" /><path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" /><path d="M6 6v14a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V6" /></>} />;

const MyCardsModal = ({
  onClose,
  currentDeckId,
  onDeckChange,
  knownChars,
  learningChars,
  isPro,
  isAuthenticated,
  customCards,
  onAddCustomCard,
  onDeleteCustomCard,
  onResetProgress,
  deckData,
}) => {
  const [activeTab, setActiveTab] = useState('decks');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCard, setNewCard] = useState({ char: '', pinyin: '', meaning: '', example: '' });
  const [addError, setAddError] = useState('');

  const handleAddCard = () => {
    setAddError('');

    if (!newCard.char.trim()) return setAddError('Character is required');
    if (!newCard.pinyin.trim()) return setAddError('Pinyin is required');
    if (!newCard.meaning.trim()) return setAddError('Meaning is required');

    if (isDuplicateCard(newCard.char)) {
      return setAddError('That character is already in the Characters deck');
    }
    if (customCards.some(c => c.char === newCard.char)) {
      return setAddError('You already have a card for that character');
    }
    if (!isPro && customCards.length >= FREE_CUSTOM_LIMIT) {
      return setAddError(`Free accounts hold ${FREE_CUSTOM_LIMIT} custom cards. Pro removes the limit.`);
    }

    onAddCustomCard({ ...newCard, id: Date.now(), createdAt: new Date().toISOString() });
    setNewCard({ char: '', pinyin: '', meaning: '', example: '' });
    setShowAddForm(false);
  };

  const closeAddForm = () => {
    setShowAddForm(false);
    setNewCard({ char: '', pinyin: '', meaning: '', example: '' });
    setAddError('');
  };

  /* ---------------- Decks ---------------- */

  const renderDecks = () => {
    const deckIds = Object.keys(DECKS).filter(id => {
      const config = DECKS[id];
      if (config.requiresAuth && !isAuthenticated) return false;
      return id !== 'custom';
    });

    return (
      <div className="mc-body">
        <h3 className="mc-heading">Select a deck</h3>

        <div className="mc-list">
          {deckIds.map(deckId => {
            const config = DECKS[deckId];
            const stats = getDeckStats(deckId, isPro);
            const isActive = currentDeckId === deckId;
            const isLimited = stats.available < stats.total;

            return (
              <button
                key={deckId}
                onClick={() => onDeckChange(deckId)}
                className={`mc-deck${isActive ? ' is-active' : ''}`}
                style={{ '--deck-color': config.color }}
                aria-current={isActive ? 'true' : undefined}
              >
                <span className="mc-deck-glyph">{config.icon}</span>

                <span className="mc-deck-info">
                  <span className="mc-deck-name">
                    {config.name}
                    <span className="mc-deck-cn">{config.nameCN}</span>
                  </span>
                  <span className="mc-deck-meta">
                    {stats.available} / {stats.total} cards
                    {isLimited && (
                      <span className="mc-lock">
                        <IconLock size={13} width={2.6} /> Pro unlocks all
                      </span>
                    )}
                  </span>
                </span>

                {isActive && (
                  <span className="mc-check" aria-hidden="true">
                    <IconCheck size={15} width={3.2} />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {currentDeckId === 'characters' && customCards.length > 0 && (
          <p className="mc-note">
            <IconInfo size={16} />
            Your {customCards.length} custom card{customCards.length > 1 ? 's are' : ' is'} mixed into this deck.
          </p>
        )}
      </div>
    );
  };

  /* ---------------- Progress ---------------- */

  const renderChips = (set, tone) => {
    if (set.size === 0) {
      return <p className="mc-empty-line">Nothing here yet — start swiping.</p>;
    }
    const shown = [...set].sort((a, b) => a - b).slice(0, 50);
    return (
      <div className="mc-chips">
        {shown.map(idx => {
          const card = deckData?.[idx];
          if (!card) return null;
          return (
            <span key={idx} className={`mc-chip mc-chip-${tone}`} title={`${card.pinyin} — ${card.meaning}`}>
              {card.char}
            </span>
          );
        })}
        {set.size > 50 && <span className="mc-chip-more">+{set.size - 50} more</span>}
      </div>
    );
  };

  const renderProgress = () => {
    const config = DECKS[currentDeckId];
    if (!config) return null;

    return (
      <div className="mc-body">
        <div className="mc-stat-grid">
          <div className="mc-stat mc-stat-yes">
            <span className="mc-stat-num">{knownChars.size}</span>
            <span className="mc-stat-label">Known</span>
          </div>
          <div className="mc-stat mc-stat-no">
            <span className="mc-stat-num">{learningChars.size}</span>
            <span className="mc-stat-label">Learning</span>
          </div>
        </div>

        <h3 className="mc-heading">Known ({knownChars.size})</h3>
        {renderChips(knownChars, 'yes')}

        <h3 className="mc-heading">Learning ({learningChars.size})</h3>
        {renderChips(learningChars, 'no')}

        {(knownChars.size > 0 || learningChars.size > 0) && (
          <button className="rp-btn mc-reset" onClick={onResetProgress}>
            Reset progress for {config.name}
          </button>
        )}
      </div>
    );
  };

  /* ---------------- Custom ---------------- */

  const renderCustom = () => {
    if (!isAuthenticated) {
      return (
        <div className="mc-body mc-gate">
          <span className="mc-gate-icon"><IconLock size={26} width={2.2} /></span>
          <h3 className="rp-title">Sign in required</h3>
          <p className="rp-text">Create a free account to add cards of your own.</p>
        </div>
      );
    }

    const canAddMore = isPro || customCards.length < FREE_CUSTOM_LIMIT;

    return (
      <div className="mc-body">
        <div className="mc-custom-head">
          <div>
            <h3 className="mc-heading mc-heading-tight">
              Custom cards ({customCards.length}{!isPro && `/${FREE_CUSTOM_LIMIT}`})
            </h3>
            {!isPro && <p className="mc-sub">Free accounts hold {FREE_CUSTOM_LIMIT}.</p>}
          </div>

          {canAddMore && !showAddForm && (
            <button className="rp-btn rp-btn-lime mc-add" onClick={() => setShowAddForm(true)}>
              <IconPlus size={16} width={3} /> Add
            </button>
          )}
        </div>

        {showAddForm && (
          <div className="mc-form">
            <label className="mc-label" htmlFor="mc-char">Character *</label>
            <input id="mc-char" className="mc-input mc-input-hanzi" value={newCard.char}
              onChange={(e) => setNewCard({ ...newCard, char: e.target.value })} placeholder="汉" />

            <label className="mc-label" htmlFor="mc-pinyin">Pinyin *</label>
            <input id="mc-pinyin" className="mc-input" value={newCard.pinyin}
              onChange={(e) => setNewCard({ ...newCard, pinyin: e.target.value })} placeholder="hàn" />

            <label className="mc-label" htmlFor="mc-meaning">Meaning *</label>
            <input id="mc-meaning" className="mc-input" value={newCard.meaning}
              onChange={(e) => setNewCard({ ...newCard, meaning: e.target.value })} placeholder="Chinese; Han" />

            <label className="mc-label" htmlFor="mc-example">Example (optional)</label>
            <input id="mc-example" className="mc-input" value={newCard.example}
              onChange={(e) => setNewCard({ ...newCard, example: e.target.value })}
              placeholder="汉字 (hànzì) — Chinese characters" />

            {addError && <p className="mc-error" role="alert">{addError}</p>}

            <div className="mc-form-actions">
              <button className="rp-btn" onClick={closeAddForm}>Cancel</button>
              <button className="rp-btn rp-btn-lime" onClick={handleAddCard}>Add card</button>
            </div>
          </div>
        )}

        {customCards.length === 0 ? (
          <div className="mc-gate">
            <span className="mc-gate-icon"><IconPlus size={26} width={2.6} /></span>
            <p className="rp-text">No custom cards yet. Add the characters you keep forgetting.</p>
          </div>
        ) : (
          <div className="mc-list">
            {customCards.map(card => (
              <div key={card.id} className="mc-card-row">
                <span className="mc-card-glyph">{card.char}</span>
                <span className="mc-card-info">
                  <span className="mc-card-pinyin">{card.pinyin}</span>
                  <span className="mc-card-meaning">{card.meaning}</span>
                </span>
                <button
                  className="mc-card-del"
                  onClick={() => onDeleteCustomCard(card.id)}
                  aria-label={`Delete ${card.char}`}
                >
                  <IconTrash size={16} width={2.4} />
                </button>
              </div>
            ))}
          </div>
        )}

        {customCards.length > 0 && (
          <p className="mc-note">
            <IconInfo size={16} />
            Custom cards are mixed into the Characters deck.
          </p>
        )}
      </div>
    );
  };

  const tabs = [
    { id: 'decks', label: 'Decks', Glyph: IconDecks },
    { id: 'progress', label: 'Progress', Glyph: IconProgress },
    { id: 'custom', label: 'Custom', Glyph: IconPlus },
  ];

  return (
    <div className="rp-overlay" role="dialog" aria-modal="true" aria-labelledby="mc-title">
      <div className="rp-modal rp-modal-wide">
        <div className="mc-header">
          <h2 id="mc-title" className="rp-title">My Cards</h2>
          <button className="rp-close" onClick={onClose} aria-label="Close">
            <IconClose size={17} width={3} />
          </button>
        </div>

        <div className="mc-tabs" role="tablist">
          {tabs.map(({ id, label, Glyph }) => (
            <button
              key={id}
              role="tab"
              aria-selected={activeTab === id}
              className={`mc-tab${activeTab === id ? ' is-active' : ''}`}
              onClick={() => setActiveTab(id)}
            >
              <Glyph size={17} />
              {label}
            </button>
          ))}
        </div>

        <div className="mc-content">
          {activeTab === 'decks' && renderDecks()}
          {activeTab === 'progress' && renderProgress()}
          {activeTab === 'custom' && renderCustom()}
        </div>
      </div>
    </div>
  );
};

export default MyCardsModal;
